import { 
  Button, 
  Card, 
  Col, 
  Divider, 
  Layout, 
  Row, 
  Space, 
  Typography, 
  Image, 
  Modal,
  Statistic,
  Steps,
  Avatar,
  Tag,
  Carousel,
  Tabs
} from 'antd';
import { 
  BarChartOutlined, 
  ShopOutlined, 
  DatabaseOutlined, 
  TeamOutlined, 
  SyncOutlined, 
  PlayCircleOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  LineChartOutlined,
  AppstoreOutlined,
  CustomerServiceOutlined,
  SafetyOutlined,
  CloudOutlined,
  StarFilled,
  ArrowRightOutlined,
  MobileOutlined,
  DesktopOutlined
} from '@ant-design/icons';
import AppHeader from './Header';
import AppFooter from './Footer';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;
const { Step } = Steps;
const { TabPane } = Tabs;

const featureCards = [
  {
    icon: <BarChartOutlined style={{ fontSize: 36, color: '#0d306d' }} />,
    title: "Real-time Analytics",
    description: "Comprehensive dashboards with live sales data and performance metrics",
    color: '#0d306d'
  },
  {
    icon: <ShopOutlined style={{ fontSize: 36, color: '#ff4f2e' }} />,
    title: "Inventory Control",
    description: "Automated stock tracking with intelligent replenishment alerts",
    color: '#ff4f2e'
  },
  {
    icon: <DatabaseOutlined style={{ fontSize: 36, color: '#0d306d' }} />,
    title: "Warehouse Sync",
    description: "Seamless integration between retail stores and warehouse inventory",
    color: '#0d306d'
  },
  {
    icon: <TeamOutlined style={{ fontSize: 36, color: '#ff4f2e' }} />,
    title: "Team Management",
    description: "Role-based access control for staff with customizable permissions",
    color: '#ff4f2e'
  },
  {
    icon: <SyncOutlined style={{ fontSize: 36, color: '#0d306d' }} />,
    title: "Cloud Sync",
    description: "Real-time data synchronization across all devices and locations",
    color: '#0d306d'
  },
  {
    icon: <SafetyOutlined style={{ fontSize: 36, color: '#ff4f2e' }} />,
    title: "Data Security",
    description: "Enterprise-grade security with automatic backups and encryption",
    color: '#ff4f2e'
  }
];

const testimonials = [
  {
    name: "Juma Mwinyi",
    role: "Retail Store Owner, Dar es Salaam",
    content: "SellSmart transformed our business operations. Inventory management that used to take hours now happens automatically.",
    avatar: "/images/avatar-african1.jpg"
  },
  {
    name: "Neema Charles",
    role: "Restaurant Manager, Nairobi",
    content: "The real-time reporting helps us make data-driven decisions that increased our profits by 22% in three months.",
    avatar: "/images/avatar-african2.jpg"
  },
  {
    name: "Rajabu Hassan",
    role: "Chain Store Director, Mombasa",
    content: "Managing multiple locations became effortless with SellSmart's centralized dashboard and warehouse integration.",
    avatar: "/images/avatar-african3.jpg"
  },
  {
    name: "Fatuma Ali",
    role: "Supermarket Owner, Kampala",
    content: "Since using SellSmart, we've reduced stockouts by 60% and increased sales by 35% through better inventory management.",
    avatar: "/images/avatar-african4.jpg"
  }
];

const businessTypes = [
  {
    name: "Retail Shops",
    image: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
  },
  {
    name: "Restaurants",
    image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
  },
  {
    name: "Supermarkets",
    image: "https://images.unsplash.com/photo-1550583724-b2692b85b150?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80"
  },
  {
    name: "Wholesale Stores",
    image: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [isDemoVisible, setIsDemoVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const showDemo = () => {
    setIsDemoVisible(true);
  };

  const handleCancel = () => {
    setIsDemoVisible(false);
  };

  return (
    <Layout className="landing-page" style={{ background: '#fff' }}>
      <AppHeader />
      
      <Content>
        {/* Hero Section */}
        <section style={{ 
          padding: '160px 0 100px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%)',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto',
            padding: '0 24px',
            position: 'relative',
            zIndex: 2
          }}>
            <Row gutter={[48, 32]} align="middle">
              <Col xs={24} md={12}>
                <div style={{ 
                  opacity: loading ? 0 : 1,
                  transform: loading ? 'translateY(20px)' : 'translateY(0)',
                  transition: 'all 0.8s ease'
                }}>
                  <Title level={1} style={{ 
                    fontSize: '48px',
                    lineHeight: '1.2',
                    marginBottom: '24px',
                    color: '#0d306d'
                  }}>
                    Transform Your Business with <span style={{ color: '#ff4f2e' }}>SellSmart</span>
                  </Title>
                  <Paragraph style={{ 
                    fontSize: '18px',
                    color: '#595959',
                    marginBottom: '40px',
                    maxWidth: '90%'
                  }}>
                  Retail management solution designed for businesses of all sizes. 
                    Streamline operations, boost sales, and gain real-time insights tailored for markets.
                  </Paragraph>
                  <Space size="large" style={{ marginBottom: '40px' }}>
                    <Button
                      type="primary"
                      size="large"
                      shape="round"
                      onClick={() => navigate("/in-touch/register")}
                      style={{
                        height: '50px',
                        padding: '0 32px',
                        fontSize: '16px',
                        fontWeight: '500',
                        background: '#ff4f2e',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(255, 79, 46, 0.3)'
                      }}
                    >
                      Start Free Trial
                    </Button>
                    <Button
                      size="large"
                      shape="round"
                      icon={<PlayCircleOutlined />}
                      onClick={showDemo}
                      style={{
                        height: '50px',
                        padding: '0 32px',
                        fontSize: '16px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#0d306d',
                        borderColor: '#0d306d'
                      }}
                    >
                      Watch Demo
                    </Button>
                  </Space>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Avatar.Group>
                      <Avatar src="/images/avatar-african1.jpg" />
                      <Avatar src="/images/avatar-african2.jpg" />
                      <Avatar src="/images/avatar-african3.jpg" />
                      <Avatar src="/images/avatar-african4.jpg" />
                      <Avatar>+42</Avatar>
                    </Avatar.Group>
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                      <b>4.9/5</b> rating from 142 reviews
                    </Text>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{
                  opacity: loading ? 0 : 1,
                  transform: loading ? 'translateY(20px)' : 'translateY(0)',
                  transition: 'all 0.8s ease 0.2s',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  height: '400px',
                  position: 'relative'
                }}>
                  <Image
                    src="/images/poster2.jpg"
                    preview={false}
                    style={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80";
                    }}
                  />
                </div>
              </Col>
            </Row>
          </div>
          <div style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(13, 48, 109, 0.08) 0%, rgba(255, 79, 46, 0.08) 100%)',
            zIndex: 1
          }}></div>
        </section>

        {/* Features Section */}
        <section style={{ 
          padding: '100px 0 80px',
          background: '#fff'
        }}>
          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto',
            padding: '0 24px'
          }}>
            <div style={{ 
              textAlign: 'center',
              marginBottom: '64px',
              opacity: loading ? 0 : 1,
              transform: loading ? 'translateY(20px)' : 'translateY(0)',
              transition: 'all 0.8s ease'
            }}>
              <Tag color="#ff4f2e" style={{ 
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                marginBottom: '16px',
                color: 'white'
              }}>
                POWERFUL FEATURES
              </Tag>
              <Title level={2} style={{ 
                fontSize: '36px',
                marginBottom: '16px',
                color: '#0d306d'
              }}>
                Everything You Need to <span style={{ color: '#ff4f2e' }}>Grow Your Business</span>
              </Title>
              <Paragraph style={{ 
                fontSize: '16px',
                color: '#595959',
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                SellSmart combines powerful retail management tools with an intuitive interface designed for businesses.
              </Paragraph>
            </div>

            <Row gutter={[24, 48]}>
              {featureCards.map((feature, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <div style={{
                    opacity: loading ? 0 : 1,
                    transform: loading ? 'translateY(20px)' : 'translateY(0)',
                    transition: `all 0.8s ease ${index * 0.1}s`
                  }}>
                    <Card
                      hoverable
                      style={{ 
                        height: '100%',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.3s',
                        ':hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                      bodyStyle={{ 
                        padding: '32px',
                        textAlign: 'center'
                      }}
                    >
                      <div style={{ 
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: `${feature.color}10`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px'
                      }}>
                        {feature.icon}
                      </div>
                      <Title level={4} style={{ marginBottom: '12px', color: '#0d306d' }}>{feature.title}</Title>
                      <Text type="secondary" style={{ fontSize: '15px' }}>{feature.description}</Text>
                    </Card>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        {/* Dashboard Showcase Section */}
        <section style={{ 
          padding: '100px 0',
          background: '#f9fafc'
        }}>
          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto',
            padding: '0 24px'
          }}>
            <div style={{ 
              textAlign: 'center',
              marginBottom: '48px'
            }}>
              <Tag color="#0d306d" style={{ 
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                marginBottom: '16px',
                color: 'white'
              }}>
                INTUITIVE INTERFACE
              </Tag>
              <Title level={2} style={{ 
                fontSize: '36px',
                marginBottom: '16px',
                color: '#0d306d'
              }}>
                Beautiful Dashboard <span style={{ color: '#ff4f2e' }}>On Any Device</span>
              </Title>
              <Paragraph style={{ 
                fontSize: '16px',
                color: '#595959',
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                Access your business data anywhere with our responsive design that works perfectly on all devices.
              </Paragraph>
            </div>

            <Tabs defaultActiveKey="1" centered style={{ marginBottom: '40px' }}>
              <TabPane 
                tab={
                  <span>
                    <DesktopOutlined /> Desktop View
                  </span>
                } 
                key="1"
              >
                <div style={{
                  position: 'relative',
                  padding: '40px 0',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%)',
                  borderRadius: '12px',
                  margin: '0 auto',
                  maxWidth: '900px'
                }}>
                  <div style={{
                    position: 'relative',
                    width: '90%',
                    margin: '0 auto',
                    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {/* Desktop browser mockup chrome */}
                    <div style={{
                      height: '36px',
                      background: '#e8e8e8',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 12px',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px'
                    }}>
                      <div style={{
                        display: 'flex',
                        gap: '8px'
                      }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ff5f56' }}></div>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ffbd2e' }}></div>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#27c93f' }}></div>
                      </div>
                    </div>
                    {/* Your dashboard screenshot */}
                    <Image
                      src="/images/dashboard-desktop-screenshot.png"
                      preview={false}
                      style={{
                        width: '100%',
                        display: 'block',
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: '8px'
                      }}
                    />
                  </div>
                </div>
              </TabPane>
              <TabPane 
                tab={
                  <span>
                    <MobileOutlined /> Mobile View
                  </span>
                } 
                key="2"
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '40px 0'
                }}>
                  <div style={{
                    position: 'relative',
                    width: '300px',
                    height: '600px',
                    borderRadius: '40px',
                    background: '#f5f7fa',
                    padding: '80px 16px',
                    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
                    border: '12px solid #222'
                  }}>
                    {/* Mobile notch */}
                    <div style={{
                      position: 'absolute',
                      top: '20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '120px',
                      height: '24px',
                      background: '#222',
                      borderRadius: '0 0 12px 12px'
                    }}></div>
                    {/* Your mobile dashboard screenshot */}
                    <div style={{
                      height: '100%',
                      borderRadius: '24px',
                      overflow: 'hidden'
                    }}>
                      <Image
                        src="/images/dashboard-mobile-screenshot.png"
                        preview={false}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </TabPane>
            </Tabs>

            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              <Button
                type="primary"
                size="large"
                shape="round"
                onClick={showDemo}
                style={{
                  height: '50px',
                  padding: '0 32px',
                  fontSize: '16px',
                  fontWeight: '500',
                  background: '#0d306d',
                  border: 'none'
                }}
              >
                Take Interactive Product Tour
              </Button>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section style={{ 
          padding: '100px 0',
          background: '#fff'
        }}>
          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto',
            padding: '0 24px'
          }}>
            <Row gutter={[48, 48]} align="middle">
              <Col xs={24} md={12}>
                <div style={{ 
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)'
                }}>
                  <Image
                    src="https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1469&q=80"
                    preview={false}
                    style={{ 
                      width: '100%',
                      display: 'block'
                    }}
                  />
                </div>
              </Col>
              <Col xs={24} md={12}>
                <Tag color="#ff4f2e" style={{ 
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  marginBottom: '16px',
                  color: 'white'
                }}>
                  HOW IT WORKS
                </Tag>
                <Title level={2} style={{ 
                  fontSize: '36px',
                  marginBottom: '24px',
                  color: '#0d306d'
                }}>
                  Transform Your Business in <span style={{ color: '#ff4f2e' }}>3 Simple Steps</span>
                </Title>
                
                <Steps direction="vertical" current={3} style={{ marginBottom: '40px' }}>
                  <Step 
                    title="Sign Up & Setup" 
                    description="Create your account and configure your store settings in minutes" 
                    icon={<CheckCircleOutlined style={{ color: '#ff4f2e' }} />}
                  />
                  <Step 
                    title="Import Your Data" 
                    description="Bring in your existing inventory, products, and customer data" 
                    icon={<DatabaseOutlined style={{ color: '#ff4f2e' }} />}
                  />
                  <Step 
                    title="Start Selling Smarter" 
                    description="Begin managing your business with powerful analytics and tools" 
                    icon={<LineChartOutlined style={{ color: '#ff4f2e' }} />}
                  />
                </Steps>
                
                <Button
                  type="primary"
                  size="large"
                  shape="round"
                  onClick={() => navigate("/in-touch/register")}
                  style={{
                    height: '50px',
                    padding: '0 32px',
                    fontSize: '16px',
                    fontWeight: '500',
                    background: '#ff4f2e',
                    border: 'none'
                  }}
                >
                  Get Started Now
                </Button>
              </Col>
            </Row>
          </div>
        </section>

        {/* Testimonials Section */}
        <section style={{ 
          padding: '80px 0',
          background: '#f9fafc'
        }}>
          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto',
            padding: '0 24px'
          }}>
            <div style={{ 
              textAlign: 'center',
              marginBottom: '64px'
            }}>
              <Tag color="#ff4f2e" style={{ 
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                marginBottom: '16px',
                color: 'white'
              }}>
                CUSTOMER STORIES
              </Tag>
              <Title level={2} style={{ 
                fontSize: '36px',
                marginBottom: '16px',
                color: '#0d306d'
              }}>
                Trusted by <span style={{ color: '#ff4f2e' }}>Businesses Across Countries</span>
              </Title>
              <Paragraph style={{ 
                fontSize: '16px',
                color: '#595959',
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                Don't just take our word for it. Here's what our customers have to say.
              </Paragraph>
            </div>

            <Carousel 
              autoplay 
              dots={{ className: 'testimonial-dots' }}
              style={{ margin: '0 -12px' }}
            >
              {testimonials.map((testimonial, index) => (
                <div key={index} style={{ padding: '0 12px' }}>
                  <Card
                    style={{ 
                      height: '100%',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                      borderRadius: '12px'
                    }}
                    bodyStyle={{ 
                      padding: '32px'
                    }}
                  >
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '24px'
                    }}>
                      <Avatar 
                        src={testimonial.avatar} 
                        size={64}
                        style={{ marginRight: '16px' }}
                      />
                      <div>
                        <Title level={5} style={{ marginBottom: '4px', color: '#0d306d' }}>{testimonial.name}</Title>
                        <Text type="secondary">{testimonial.role}</Text>
                      </div>
                    </div>
                    <Paragraph style={{ 
                      fontSize: '16px',
                      color: '#595959',
                      fontStyle: 'italic'
                    }}>
                      "{testimonial.content}"
                    </Paragraph>
                    <div style={{ marginTop: '16px' }}>
                      {[...Array(5)].map((_, i) => (
                        <StarFilled key={i} style={{ color: '#ff4f2e' }} />
                      ))}
                    </div>
                  </Card>
                </div>
              ))}
            </Carousel>

            <style jsx global>{`
              .testimonial-dots li button {
                background: #0d306d !important;
                opacity: 0.3;
              }
              .testimonial-dots li.slick-active button {
                background: #ff4f2e !important;
                opacity: 1;
              }
            `}</style>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{ 
          padding: '100px 0',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%)',
          textAlign: 'center'
        }}>
          <div style={{ 
            maxWidth: '800px', 
            margin: '0 auto',
            padding: '0 24px'
          }}>
            <Tag color="#ff4f2e" style={{ 
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '14px',
              marginBottom: '16px',
              color: 'white'
            }}>
              READY TO GROW?
            </Tag>
            <Title level={2} style={{ 
              fontSize: '36px',
              marginBottom: '24px',
              color: '#0d306d'
            }}>
              Start Your <span style={{ color: '#ff4f2e' }}>Free 14-Day Trial</span> Today
            </Title>
            <Paragraph style={{ 
              fontSize: '18px',
              color: '#595959',
              marginBottom: '40px'
            }}>
              No credit card required. Get full access to all features with no limitations during your trial.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              shape="round"
              onClick={() => navigate("/in-touch/register?isTrial=1")}
              style={{
                height: '50px',
                padding: '0 32px',
                fontSize: '16px',
                fontWeight: '500',
                background: '#ff4f2e',
                border: 'none',
                boxShadow: '0 4px 12px rgba(255, 79, 46, 0.3)'
              }}
            >
              Start Free Trial
            </Button>
            <Paragraph style={{ 
              fontSize: '14px',
              color: '#8c8c8c',
              marginTop: '16px'
            }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
              Cancel anytime. No hidden fees.
            </Paragraph>
          </div>
        </section>
      </Content>

      <AppFooter />

      {/* Demo Video Modal */}
      <Modal
        title="SellSmart Product Demo"
        visible={isDemoVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        centered
        destroyOnClose
        bodyStyle={{ padding: 0, height: '450px' }}
      >
        <Image
          src="/images/poster2.jpg"
          preview={false}
          style={{ 
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80";
          }}
        />
      </Modal>
    </Layout>
  );
};

export default LandingPage;